import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppNavbar } from '@/components/layout/AppNavbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ArrowLeft, ChevronDown, Beaker, Tag, Package, Factory, Award } from 'lucide-react';

// Mock data - will be replaced with real data from API
const mockReviewData = {
  id: '1',
  projectTitle: 'GreenClean Floor Cleaner Certification',
  clientName: 'EcoProducts Inc.',
  status: 'in_progress' as const,
  submittedDate: '2024-01-15',
  targetCompletionDate: '2024-03-15',
  product: {
    name: 'GreenClean Floor Cleaner',
    description: 'Eco-friendly floor cleaning solution for commercial and residential use',
    manufacturer: 'EcoProducts Inc.',
    productType: 'Cleaning Product',
  },
  formulation: {
    totalIngredients: 8,
    waterContent: 85.5,
    activeIngredients: [
      { name: 'Sodium Lauryl Sulfate', percentage: 5.2, casNumber: '151-21-3' },
      { name: 'Citric Acid', percentage: 3.1, casNumber: '77-92-9' },
      { name: 'Essential Oils', percentage: 1.8, casNumber: 'Various' },
    ],
    preservatives: [
      { name: 'Phenoxyethanol', percentage: 0.5, casNumber: '122-99-6' },
    ],
  },
  chemicalProperties: {
    ph: 6.8,
    viscosity: '15 cP at 25°C',
    density: '1.02 g/cm³',
    flashPoint: 'None',
    stability: 'Stable under normal conditions',
  },
};

const ReviewDetail = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppNavbar />
      
      <main className="flex-1 container py-8">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/reviewer')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {mockReviewData.projectTitle}
              </h1>
              <p className="text-muted-foreground">
                Client: {mockReviewData.clientName} | Submitted: {mockReviewData.submittedDate}
              </p>
            </div>
            <Badge className={getStatusColor(mockReviewData.status)}>
              {mockReviewData.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>

        {/* Product Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Product Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-sm text-muted-foreground mb-1">Product Name</h3>
                <p className="font-medium">{mockReviewData.product.name}</p>
              </div>
              <div>
                <h3 className="font-medium text-sm text-muted-foreground mb-1">Manufacturer</h3>
                <p className="font-medium">{mockReviewData.product.manufacturer}</p>
              </div>
              <div>
                <h3 className="font-medium text-sm text-muted-foreground mb-1">Product Type</h3>
                <p className="font-medium">{mockReviewData.product.productType}</p>
              </div>
              <div>
                <h3 className="font-medium text-sm text-muted-foreground mb-1">Target Completion</h3>
                <p className="font-medium">{mockReviewData.targetCompletionDate}</p>
              </div>
            </div>
            {mockReviewData.product.description && (
              <div className="mt-4">
                <h3 className="font-medium text-sm text-muted-foreground mb-1">Description</h3>
                <p className="text-sm">{mockReviewData.product.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Review Sections */}
        <div className="space-y-4">
          {/* Chemical Review */}
          <Collapsible defaultOpen>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Beaker className="h-5 w-5" />
                      Chemical Review
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-6">
                    {/* Formulation Details */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Formulation Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-primary">{mockReviewData.formulation.totalIngredients}</p>
                          <p className="text-sm text-muted-foreground">Total Ingredients</p>
                        </div>
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-primary">{mockReviewData.formulation.waterContent}%</p>
                          <p className="text-sm text-muted-foreground">Water Content</p>
                        </div>
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-primary">{mockReviewData.formulation.activeIngredients.length}</p>
                          <p className="text-sm text-muted-foreground">Active Ingredients</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium mb-2">Active Ingredients</h4>
                          <div className="space-y-2">
                            {mockReviewData.formulation.activeIngredients.map((ingredient, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-muted rounded">
                                <div>
                                  <p className="font-medium text-sm">{ingredient.name}</p>
                                  <p className="text-xs text-muted-foreground">CAS: {ingredient.casNumber}</p>
                                </div>
                                <span className="font-medium">{ingredient.percentage}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-medium mb-2">Preservatives</h4>
                          <div className="space-y-2">
                            {mockReviewData.formulation.preservatives.map((preservative, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-muted rounded">
                                <div>
                                  <p className="font-medium text-sm">{preservative.name}</p>
                                  <p className="text-xs text-muted-foreground">CAS: {preservative.casNumber}</p>
                                </div>
                                <span className="font-medium">{preservative.percentage}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Chemical & Physical Properties */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Chemical & Physical Properties</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <h4 className="font-medium text-sm text-muted-foreground mb-1">pH</h4>
                          <p className="font-medium">{mockReviewData.chemicalProperties.ph}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-muted-foreground mb-1">Viscosity</h4>
                          <p className="font-medium">{mockReviewData.chemicalProperties.viscosity}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-muted-foreground mb-1">Density</h4>
                          <p className="font-medium">{mockReviewData.chemicalProperties.density}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-muted-foreground mb-1">Flash Point</h4>
                          <p className="font-medium">{mockReviewData.chemicalProperties.flashPoint}</p>
                        </div>
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-sm text-muted-foreground mb-1">Stability</h4>
                          <p className="font-medium">{mockReviewData.chemicalProperties.stability}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Label Review */}
          <Collapsible>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tag className="h-5 w-5" />
                      Label Review
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <p className="text-muted-foreground">Label review functionality will be added in future updates.</p>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Packaging Review */}
          <Collapsible>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Packaging Review
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <p className="text-muted-foreground">Packaging review functionality will be added in future updates.</p>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Manufacturing Review */}
          <Collapsible>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Factory className="h-5 w-5" />
                      Manufacturing Review
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <p className="text-muted-foreground">Manufacturing review functionality will be added in future updates.</p>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Claims Review */}
          <Collapsible>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      Claims Review
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <p className="text-muted-foreground">Claims review functionality will be added in future updates.</p>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </main>
    </div>
  );
};

export default ReviewDetail;